import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import TextField from "components/TextField";
import Button from "components/Button";
import { medium, large } from "styles/breakpoints";
import Textarea from "components/Textarea";
import * as yup from "yup";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: flex-start;
  width: 100%;
  & .fields-wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem 2.5rem;
    width: 100%;
    ${medium(`
            grid-template-columns: repeat(2, 1fr);
        `)}
  }
  & .fields {
    width: 100%;
    ${large(`
            width: 70%;
        `)}
  }
`;

const FieldOptions = {
  text: (args) => <TextField {...args} type="text" />,
  textarea: (args) => <Textarea {...args} />,
  number: (args) => <TextField {...args} type="number" />,
  date: () => {},
  select: () => {},
  checkbox: () => {},
  radio: () => {},
  email: (args) => <TextField {...args} type="email" />,
  default: (args) => <TextField {...args} type="text" />,
};

const Form = ({
  children,
  className = "",
  fields,
  id = "",
  loading,
  nativeSubmit,
  onSubmit,
  style = {},
  submitText = "Submit",
  validations = {},
  validateOnChange = true,
  wrap = true,
  inheritFont = false,
  ...rest
}) => {
  const [errors, setErrors] = useState({});
  const [isValidForm, setIsValidForm] = useState(!validateOnChange);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validateFormFn = useCallback(
    async (newValues) => {
      const valid = await yup
        .object()
        .shape({
          ...validations,
        })
        .isValid(newValues);
      setIsValidForm(valid);
      return valid;
    },
    [validations]
  );

  useEffect(() => {
    if (fields.length) {
      fields.forEach((field) => {
        setErrors((prevState) => ({
          ...prevState,
          [field.id]: null,
        }));
      });
    }
  }, [fields]);

  const validateForm = useCallback(
    async (fieldId, value) => {
      if (!validations?.[fieldId]) return true;
      try {
        await validations[fieldId].validate(value);
        setErrors((prevState) => ({
          ...prevState,
          [fieldId]: null,
        }));
        return true;
      } catch (error) {
        setErrors((prevState) => ({
          ...prevState,
          [fieldId]: error.errors?.[0],
        }));
        return false;
      }
    },
    [validations]
  );

  const handleChange = async (key, value) => {
    const newValues = {
      ...values,
      [key]: value,
    };
    setValues(newValues);
    if (validateOnChange) {
      await validateFormFn(newValues);
      await validateForm(key, value);
    } else if (errors[key]) {
      setErrors((prevState) => ({ ...prevState, [key]: null }));
    }
  };

  const handleSubmit = async (ev) => {
    if (nativeSubmit) {
      if (onSubmit) return onSubmit(ev);
      return ev;
    }
    ev.preventDefault();
    if (!validateOnChange) {
      setSubmitting(true);
      const results = await Promise.all(
        fields.map((field) => validateForm(field.id, values[field.id]))
      );
      setSubmitting(false);
      if (results.includes(false)) return;
    }
    onSubmit?.(values);
  };

  const noopValidate = useCallback(() => {}, []);

  return (
    <FormStyled
      className={className}
      id={id}
      onSubmit={handleSubmit}
      style={style}
      {...rest}
    >
      <div className={wrap ? "fields-wrapper" : "fields"}>
        {fields.map((field) => {
          const { type, value, ...restField } = field;
          const Element = FieldOptions[type] || FieldOptions.default;
          return (
            <Element
              key={field.id}
              error={errors[field.id]}
              item={{ value: values[field.id] || field.value, ...restField }}
              gap={rest.gap}
              onChange={handleChange}
              validateForm={validateOnChange ? validateForm : noopValidate}
              inheritFont={inheritFont}
            />
          );
        })}
      </div>
      {children && <div className="children-wrapper">{children}</div>}
      <Button disabled={!isValidForm || loading || submitting} type="submit">
        {submitting ? "Checking…" : submitText}
      </Button>
    </FormStyled>
  );
};

export default Form;

Form.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      fullSize: PropTypes.bool,
      id: PropTypes.string,
      label: PropTypes.string,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      type: PropTypes.oneOf([
        "text",
        "textarea",
        "number",
        "date",
        "select",
        "checkbox",
        "radio",
        "email",
      ]),
      values: PropTypes.string,
    })
  ),
  id: PropTypes.string,
  nativeSubmit: PropTypes.bool,
  onSubmit: PropTypes.func,
  style: PropTypes.object,
  submitText: PropTypes.string,
  validations: PropTypes.object,
};
